import {useRef} from 'react';
import {Box} from '@mui/material';
import {t} from 'i18next';

import {ArrMenuHandle, CustomMenuItem} from '../../../common/arr-menu/ArrMenu';
import {
  ReportActionMenuOptionTypes,
  RoleId,
  SourceTypeCode,
} from '../../../../shared/constants';
import './ActionMenu.scss';
import ActionMenuWrapper from '../../../action-menu/action-menu-wrapper/ActionMenuWrapper';
import {BulkActionContextItem} from '../../types/SessionSearch';

export interface ActionMenuProps {
  userRoleId: number;
  disabled: boolean;
  onClickActionItem?: (item: CustomMenuItem) => void;
  lstBulkActionContext?: BulkActionContextItem[];
  documentCount?: string | number;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  userRoleId,
  disabled,
  onClickActionItem,
  lstBulkActionContext,
  documentCount,
}) => {
  const actionMenuRef = useRef<ArrMenuHandle>(null);

  const getActionMenuItems = (roleId: number): CustomMenuItem[] => {
    const isRMU = roleId === Number(RoleId.ReviewManager);
    const isPAU = roleId === Number(RoleId.ProjectAdministrator);
    const isReviewer = roleId === Number(RoleId.Reviewer);

    const viewMenu = {
      id: 'view',
      label: t('sessionSearch.actions.view'),
      submenu: [
        {
          id: 'view-in-doc-view',
          label: t('sessionSearch.actions.viewInDocView'),
        },
        {
          id: 'view-in-doc-view-new-tab',
          label: t('sessionSearch.actions.viewInDocViewInNewTab'),
        },
        {
          id: 'view-in-doc-list',
          label: t('sessionSearch.actions.viewInDocList'),
        },
      ],
    };

    if (isRMU) {
      return [
        viewMenu,
        {
          id: ReportActionMenuOptionTypes.BulkTag,
          label: t('sessionSearch.actions.bulkTag'),
        },
        {
          id: ReportActionMenuOptionTypes.BulkFolder,
          label: t('sessionSearch.actions.bulkFolder'),
        },
        {
          id: ReportActionMenuOptionTypes.BulkAssign,
          label: t('sessionSearch.actions.bulkAssign'),
        },
        {
          id: ReportActionMenuOptionTypes.QuickBatch,
          label: t('sessionSearch.actions.quickBatch'),
        },
        {id: 'tally', label: t('sessionSearch.actions.tally')},
        {id: 'export-data', label: t('sessionSearch.actions.exportData')},
        {
          id: 'communication-explorer',
          label: t('sessionSearch.actions.communicationExplorer'),
        },
        {
          id: 'concept-explorer',
          label: t('sessionSearch.actions.conceptExplorer'),
        },
      ];
    }

    if (isPAU) {
      return [
        viewMenu,
        {
          id: ReportActionMenuOptionTypes.BulkTag,
          label: t('sessionSearch.actions.bulkTag'),
        },
        {
          id: ReportActionMenuOptionTypes.BulkFolder,
          label: t('sessionSearch.actions.bulkFolder'),
        },
        {
          id: ReportActionMenuOptionTypes.BulkRelease,
          label: t('sessionSearch.actions.bulkRelease'),
        },
        {id: 'tally', label: t('sessionSearch.actions.tally')},
        {id: 'export-data', label: t('sessionSearch.actions.exportData')},
        {
          id: 'communication-explorer',
          label: t('sessionSearch.actions.communicationExplorer'),
        },
        {
          id: 'concept-explorer',
          label: t('sessionSearch.actions.conceptExplorer'),
        },
      ];
    }

    if (isReviewer) {
      return [
        viewMenu,
        {
          id: ReportActionMenuOptionTypes.BulkTag,
          label: t('sessionSearch.actions.bulkTag'),
        },
        {
          id: ReportActionMenuOptionTypes.BulkFolder,
          label: t('sessionSearch.actions.bulkFolder'),
        },
        {
          id: 'communication-explorer',
          label: t('sessionSearch.actions.communicationExplorer'),
          disabled: true,
        },
        {
          id: 'concept-explorer',
          label: t('sessionSearch.actions.conceptExplorer'),
          disabled: true,
        },
      ];
    }

    return [];
  };

  /* ----------------------------------
   * ACTION HANDLER
   * ---------------------------------- */
  const handleActionMenuItemPick = (item: CustomMenuItem) => {
    switch (item.id) {
      case 'view-in-doc-view':
      case 'view-in-doc-view-new-tab':
        actionMenuRef.current?.close();
        break;
    }

    onClickActionItem?.(item);
  };

  const renderActionDropdown = () => {
    const menuItems = getActionMenuItems(userRoleId);
    const hasItems = menuItems.length > 0;

    return (
      <Box className="table-header-section">
        <ActionMenuWrapper
          actions={menuItems}
          size="small"
          isDisabled={disabled || !hasItems}
          menuClassName="abm-action-menu"
          sourceType={SourceTypeCode.SESSIONSEARCH}
          sourceTypeContext={SourceTypeCode.SESSIONSEARCH}
          sourceDetails={{
            LstBulkActionContext: lstBulkActionContext,
          }}
          dataTestId="session-search-action-menu"
          onActionPick={handleActionMenuItemPick}
          additionalParameters={{documentCount}}
        />
      </Box>
    );
  };

  return <Box className="action-menu">{renderActionDropdown()}</Box>;
};

export default ActionMenu;
